import { createContext, useContext, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ff, flags } from 'functions/roleBasedAccessUtils'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import moment from 'moment'

import { setOverrideOrganization, clearOverrideOrganization } from 'redux/slices/admin'
import { resetUserState } from 'redux/slices/user'
import { resetAthleteProfileState } from 'redux/slices/athleteProfile'
import { resetBrand } from 'redux/slices/brand'
import { resetGeneratorState } from 'redux/slices/generator'
import { resetOrganizationState } from 'redux/slices/organization'
import { clearPendingEvents } from 'redux/slices/pendingEvents'
import { resetTrainerState } from 'redux/slices/trainer'
import { resetTrainingChartState } from 'redux/slices/trainingChart'
import { clearWhichWeightCache } from 'redux/slices/whichWeightCache'

export const AdminContext = createContext()

export const AdminContextProvider = ({ children }) => {
    const dispatch = useDispatch()

    const userId = useSelector((state) => state.user.id)
    const userType = useSelector((state) => state.user.type)
    const adminToolsVisible = useSelector((state) => state.user.adminToolsVisible)
    const organizationId = useSelector((state) => state.user.organizationId)
    const canAccessSidebarSalesTools = ff(flags.canAccessSidebarSalesTools, { userType: userType, adminToolsVisible: adminToolsVisible })

    const { ShowModal, HideModal } = useContext(ModalContext)

    const ClearOrganizationOverride = () => {
        if (canAccessSidebarSalesTools === false) {
            return
        }

        dispatch(clearOverrideOrganization())
        dispatch(resetUserState())
        dispatch(resetAthleteProfileState())
        dispatch(resetBrand())
        dispatch(resetGeneratorState())
        dispatch(resetOrganizationState())
        dispatch(clearPendingEvents())
        dispatch(resetTrainerState())
        dispatch(resetTrainingChartState())
        dispatch(clearWhichWeightCache())
    }

    const GetOrganizationBillingConfiguration = async (setOrganizationBillingConfiguration) => {
        if (canAccessSidebarSalesTools === false) {
            return
        }
        
        const jsonBody = {
            organizationId: organizationId
        }
        
        // console.log(jsonBody)

        const result = await WhichWeightAPI('BILLING', 'GET', '/organization_billing_configuration', jsonBody)

        // console.log(result)

        if (result?.data?.status === 'failure') {
            console.error(result)
            setOrganizationBillingConfiguration(null)
            return
        }

        // console.log(result?.data?.data)

        setOrganizationBillingConfiguration(result?.data?.data ?? null)
    }

    const GetOrganizationDirectoryFromBackend = async (organizationIdFilter, organizationNameFilter, adminEmailFilter, salesRepEmailFilter, setOrganizations, abortController) => {
        if (canAccessSidebarSalesTools === false) {
            setOrganizations([])
            return
        }

        try {
            setOrganizations(null)

            const jsonBody = {}

            if (organizationIdFilter !== '') jsonBody.organizationIdFilter = organizationIdFilter
            if (organizationNameFilter !== '') jsonBody.organizationNameFilter = organizationNameFilter
            if (adminEmailFilter !== '') jsonBody.adminEmailFilter = adminEmailFilter
            if (salesRepEmailFilter !== '') jsonBody.salesRepEmailFilter = salesRepEmailFilter

            const result = await WhichWeightAPI('REPORTING', 'GET', '/organization_directory', jsonBody, { signal: abortController.signal })

            // console.log(result)

            if (result.data.status === 'failure') {
                setOrganizations([])
                return
            }

            // Only update state if the request wasn't aborted
            if (!abortController.signal.aborted){ 
                setOrganizations(result.data.data)
            }
        } catch (error) {

            // Silently handle any abort-related errors
            if (
                error.name === 'CanceledError' || 
                error.name === 'AbortError' || 
                error.code === 'ERR_CANCELED'
            ) {
                return
            }

            // Log other errors
            console.error('Error fetching organizations:', error)
        }
    }

    const SaveOrganizationBillingConfiguration = async (setSaveLoading, selectedPricingTierDiscountKey) => {
        if (canAccessSidebarSalesTools === false) {
            return
        }

        setSaveLoading(true)

        const jsonBody = {
            organizationId: organizationId,
            pricingTierDiscountKey: selectedPricingTierDiscountKey
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('BILLING', 'POST', '/organization_billing_configuration', jsonBody)

        // console.log(result)

        setSaveLoading(false)

        if (result?.data?.status === 'failure') {
            console.error(result)
            return
        }

        window.location.reload()
    }

    const ShowEditAthleteMembershipModal = (athlete) => {
        if (canAccessSidebarSalesTools === false) {
            return
        }

        // console.log(athlete)

        const modalData = {
            key: ModalKey.EditAthleteMembership,
            size: 'quarter',
            athlete: athlete
        }

        ShowModal(modalData)
    }

    const ShowEditOrganizationBillingModal = () => {
        if (canAccessSidebarSalesTools === false) {
            return
        }

        const modalData = {
            key: ModalKey.EditOrganizationBilling,
            size: 'third'
        }

        ShowModal(modalData)
    }

    const ShowOrganizationDirectoryModal = () => {
        if (canAccessSidebarSalesTools === false) {
            return
        }

        const modalData = {
            key: ModalKey.OrganizationDirectory,
            size: 'fullscreen'
        }

        ShowModal(modalData)
    }

    const SwapOrganization = (organization, embeddedInModal) => {
        if (canAccessSidebarSalesTools === false) {
            return
        }

        const swapOrganizationPayload = {
            id: organization.id,
            name: organization.name,
            logo: organization.logo || null
        }

        dispatch(setOverrideOrganization(swapOrganizationPayload))

        dispatch(resetUserState())
        dispatch(resetAthleteProfileState())
        dispatch(resetBrand())
        dispatch(resetGeneratorState())
        dispatch(resetOrganizationState())
        dispatch(clearPendingEvents())
        dispatch(resetTrainerState())
        dispatch(resetTrainingChartState())
        dispatch(clearWhichWeightCache())

        if (embeddedInModal) {
            HideModal()
        }
    }

    // USER DIRECTORY SUB CONTEXT

    const userDirectoryTabs = [
        { key: 'user-info', text: 'User Info' },
        { key: 'user-info-legacy', text: 'User Info (Legacy)' },
        // { key: 'athlete-onboarding', text: 'Athlete Onboarding' },
    ]

    const [selectedTabKey, setSelectedTabKey] = useState(userDirectoryTabs[0].key)
    const [userDirectory, setUserDirectory] = useState(null)
    const [userDirectoryFilters, setUserDirectoryFilters] = useState({})
    const [selectedUserDirectoryUsers, setSelectedUserDirectoryUsers] = useState([])
    const [userDirectoryPagination, setUserDirectoryPagination] = useState({
        page: 1,
        pageSize: 100,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    })

    const ExportNewsletterAthletes = async () => {
        try {
            const result = await WhichWeightAPI("CORE", "GET", "/user_directory_internal_athletes_export");
            
            const data = result.data.data;

            const headers = ['id', 'firstName', 'lastName', 'email', 'createdDate'];

            const csvRows = [
                headers.join(','),
                ...data.map(user => [
                    user.id,
                    `"${user.firstName || ''}"`,
                    `"${user.lastName || ''}"`,
                    `"${user.email || ''}"`,
                    `"${user.createdDate || ''}"`
                ].join(','))
            ];

            const csvContent = csvRows.join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `internal_athletes_${moment().utc().format('YYYY-MM-DD-HH-mm-ss')}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error exporting athletes:', error);
        }
    }

    const ExportNewsletterCoaches = async () => {
        try {
            const result = await WhichWeightAPI("CORE", "GET", "/user_directory_internal_coaches_export");
            
            const data = result.data.data;

            const headers = ['id', 'organizationName', 'firstName', 'lastName', 'email', 'createdDate'];

            const csvRows = [
                headers.join(','),
                ...data.map(user => [
                    user.id,
                    `"${user.organizationName || ''}"`,
                    `"${user.firstName || ''}"`,
                    `"${user.lastName || ''}"`,
                    `"${user.email || ''}"`,
                    `"${user.createdDate || ''}"`
                ].join(','))
            ];

            const csvContent = csvRows.join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `internal_coaches_${moment().utc().format('YYYY-MM-DD-HH-mm-ss')}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error exporting coaches:', error);
        }
    }

    const ExportUserDirectory = async (exportUserDirectoryFilters) => {
        try {
            // console.log(JSON.stringify(exportUserDirectoryFilters, null, 2))

            const result = await WhichWeightAPI("CORE", "GET", "/user_directory_export_data", exportUserDirectoryFilters);

            // console.log(result)

            if (result.data.status === 'failure') {
                console.error('Error exporting user list with current filters:', result.data);
                return
            }

            const csvData = result.data.data;

            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `user_list_with_current_filters_${moment().utc().format('YYYY-MM-DD-HH-mm-ss')}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error exporting user list with current filters:', error);
        }
    }

    const GetUserDirectory = async (abortController) => {
        try {
            setUserDirectory(null)

            var jsonBody = JSON.parse(JSON.stringify(userDirectoryFilters))
            jsonBody.page = userDirectoryPagination.page
            jsonBody.pageSize = userDirectoryPagination.pageSize

            // console.log(JSON.stringify(jsonBody, null, 2))

            const result = await WhichWeightAPI("CORE", "GET", "/user_directory", jsonBody, { signal: abortController.signal });

            // console.log(result)

            if (!abortController.signal.aborted) {
                setUserDirectory(result.data.data)
                setUserDirectoryPagination(result.data.pagination)
            }

        } catch (error) {
            if (
                error.name === 'CanceledError' ||
                error.name === 'AbortError' ||
                error.code === 'ERR_CANCELED'
            ) {
                return
            }

            console.error('Error fetching users:', error)
        }
    }

    const HandleUserDirectoryFiltersChange = (key, value) => {
        // console.log('HandleUserDirectoryFiltersChange', key, value)
        
        var newUserDirectoryFilters = JSON.parse(JSON.stringify(userDirectoryFilters))

        if (value === '') {
            delete newUserDirectoryFilters[key];
        }
        else {
            newUserDirectoryFilters[key] = value;
        }

        // console.log(newUserDirectoryFilters)

        setUserDirectoryFilters(newUserDirectoryFilters)
    }

    const OnUserDirectoryRowPressed = (user) => {
        if (user.type === 'trainee') {
            const url = `/athlete/${user.id}`
            window.open(url, '_blank')
        }
    }

    const ShowDeleteUserAuth0AccountModal = (user) => {
        if (user.auth0UserId === null || user.auth0UserId === '') {
            const modalData = {
                key: ModalKey.Text,
                fullscreen: false,
                size: 'quarter',
                titleText: 'Unable to delete user Auth0 account',
                bodyText: [
                    'User ' + user.firstName + ' ' + user.lastName + ' has no Auth0 account yet.',
                ]
            }

            ShowModal(modalData)
            
            return;
        }

        const modalData = {
            key: ModalKey.TextAreaAction,
            fullscreen: false,
            size: 'third',
            titleText: 'Delete User Auth0 Account?',
            bodyText: [
                'Are you sure you want to delete the Auth0 account for ' + user.firstName + ' ' + user.lastName + '?',
                'They will no longer be able to login to the app using this account.',
                'However their email address will be freed to make a new account.',
            ],
            textAreaPlaceholder: 'Please enter the reason for deleting this auth0 account',
            buttonText: 'Continue',
            buttonAction: async (textValue) => {
                await __DeleteUserAuth0Account(userId, user, textValue)
            }
        }
   
        ShowModal(modalData)
    }

    const ShowSwapAuth0AccountModal = () => {
        const modalData = {
            key: ModalKey.ActionWarning,
            fullscreen: false,
            size: 'third',
            titleText: 'Swap Auth0 Accounts?',
            bodyText: [
                'Are you sure you want to swap the Auth0 account for the following users?',
                selectedUserDirectoryUsers[0].firstName + ' ' + selectedUserDirectoryUsers[0].lastName + ' (' + selectedUserDirectoryUsers[0].email + ')',
                selectedUserDirectoryUsers[1].firstName + ' ' + selectedUserDirectoryUsers[1].lastName + ' (' + selectedUserDirectoryUsers[1].email + ')',
            ],
            buttonText: 'Swap',
            buttonAction: async () => {
                await __SwapAuth0Accounts()
            }
        }
   
        ShowModal(modalData)
    }

    const ShowTerraDisconnectionModal = (user) => {
        if (user.terraUserId === null || user.terraUserId === '') {
            return;
        }

        const modalData = {
            key: ModalKey.ActionWarning,
            fullscreen: false,
            size: 'quarter',
            titleText: 'Disconnect Terra?',
            bodyText: [
                'Are you sure you want to disconnect the Terra connection for ' + user.firstName + ' ' + user.lastName + '?',
                'Wearable data for this user will no longer be synced to Kinesis.',
            ],
            buttonText: 'Disconnect',
            buttonAction: async () => await __DisconnectTerra(user)
        }
   
        ShowModal(modalData)
    }

    const ToggleUserDirectoryUserSelection = (user) => {
        setSelectedUserDirectoryUsers((prev) => {
            const isSelected = prev.some((selectedUser) => selectedUser.id === user.id)

            if (isSelected) {
                return prev.filter((selectedUser) => selectedUser.id !== user.id)
            }

            return [...prev, user]
        })
    }

    const __DeleteUserAuth0Account = async (adminUserId, user, textValue) => {
        const jsonBody = {
            adminUserId: adminUserId,
            userId: user.id,
            descriptionText: textValue
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI("CORE", "POST", "/delete_auth0_account", jsonBody);

        // console.log(result)

        if (result.data.status === "failure") {
            console.error(result)

            const modalData = {
                key: ModalKey.Text,
                fullscreen: false,
                titleText: 'Auth0 account deletion failed',
                bodyText: [
                    'Please try again later.'
                ],
            }

            ShowModal(modalData)

            return
        }

        window.location.reload()
    }

    const __DisconnectTerra = async (user) => {
        const jsonBody = {
            userId: user.id
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI("CORE", "POST", "/deauthenticate_terra_user", jsonBody);

        // console.log(result)

        if (result.data.status === "failure") {
            console.error(result)

            const modalData = {
                key: ModalKey.Text,
                fullscreen: false,
                titleText: 'Terra disconnect failed',
                bodyText: [
                    'Failed to disconnect Terra for user ' + user.firstName + ' ' + user.lastName + '.',
                    'Please try again later.'
                ],
            }

            ShowModal(modalData)

            return
        }

        window.location.reload()
    }

    const __SwapAuth0Accounts = async () => {
        const jsonBody = {
            athleteUserId: selectedUserDirectoryUsers.filter((selectedUser) => selectedUser.type === 'trainee')[0].id,
            coachUserId: selectedUserDirectoryUsers.filter((selectedUser) => ['organization-owner', 'organization-admin', 'trainer'].includes(selectedUser.type))[0].id
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI("CORE", "POST", "/swap_auth0_accounts", jsonBody);

        // console.log(result)

        if (result.data.status === "failure") {
            console.error(result)
        }

        window.location.reload()
    }

    const UserDirectorySubContextData = {
        userDirectoryTabs: userDirectoryTabs,
        selectedTabKey: selectedTabKey,
        setSelectedTabKey: setSelectedTabKey,
        userDirectory: userDirectory,
        userDirectoryFilters: userDirectoryFilters,
        setUserDirectoryFilters: setUserDirectoryFilters,
        userDirectoryPagination: userDirectoryPagination,
        setUserDirectoryPagination: setUserDirectoryPagination,
        selectedUserDirectoryUsers: selectedUserDirectoryUsers,
        setSelectedUserDirectoryUsers: setSelectedUserDirectoryUsers,
        ExportNewsletterAthletes: ExportNewsletterAthletes,
        ExportNewsletterCoaches: ExportNewsletterCoaches,
        ExportUserDirectory: ExportUserDirectory,
        GetUserDirectory: GetUserDirectory,
        HandleUserDirectoryFiltersChange: HandleUserDirectoryFiltersChange,
        OnUserDirectoryRowPressed: OnUserDirectoryRowPressed,
        ShowDeleteUserAuth0AccountModal: ShowDeleteUserAuth0AccountModal,
        ShowSwapAuth0AccountModal: ShowSwapAuth0AccountModal,
        ShowTerraDisconnectionModal: ShowTerraDisconnectionModal,
        ToggleUserDirectoryUserSelection: ToggleUserDirectoryUserSelection
    }

    // ADMIN CONTEXT SETUP

    const AdminContextData = {
        ClearOrganizationOverride,
        GetOrganizationBillingConfiguration,
        GetOrganizationDirectoryFromBackend,
        SaveOrganizationBillingConfiguration,
        ShowEditAthleteMembershipModal,
        ShowEditOrganizationBillingModal,
        ShowOrganizationDirectoryModal,
        SwapOrganization,
        UserDirectorySubContext: UserDirectorySubContextData
    }

    return (
        <AdminContext.Provider value={AdminContextData}>
            {children}
        </AdminContext.Provider>
    )
}
